import React, {createContext, useCallback, useContext, useRef} from 'react'
import {useLocation} from 'react-router-dom'

export interface TrackedPromotionItem {
  creative_name: string
  creative_slot: string
  promotion_id: string
  promotion_name: string
  link_position: number
  slot_position: number | undefined
  campaign_ID?: string
}

interface TrackedPromotionEntry {
  slotPosition: number | undefined
  linkPosition: number
  creativeSlot: string
}

interface PromotionLookupRequest {
  creative_name: string
  promotion_id: string
  creative_slot: string
  slot_position?: number
}

interface PromotionTrackingRegistryContextValue {
  registerTrackedPromotions: (promotionItems: TrackedPromotionItem[]) => void
  findTrackedPromotion: (selectedPromotion: PromotionLookupRequest) => TrackedPromotionEntry | undefined
}

/**
 * Shared registry that lets `select_promotion` events reuse the canonical
 * `slot_position` / `link_position` / `creative_slot` values that were already
 * computed when the corresponding `view_promotion` event was dispatched.
 *
 * Without this, individual child components (CategoryTiles, ThinBanner, etc.) only know
 * their local index and cannot reproduce the page-wide ordering that
 * `createPromoForViewPromotion` builds.
 */

/**
 * Default context value used in the case when a consumer somehow calls the hook without a
 * `PromotionTrackingProvider` mounted above it. (Unlikely to ever happen as the context is mounted in the
 * route of the app)
 */
const PromotionTrackingRegistryContext = createContext<PromotionTrackingRegistryContextValue>({
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  registerTrackedPromotions: () => {},
  findTrackedPromotion: () => undefined,
})

/**
 * Build a stable key that is identical on the view event (built centrally)
 * and on the select event (built by the individual child component).
 */
const buildPromotionLookupKey = (promotion: {creative_name?: string; promotion_id?: string}): string =>
  `${promotion?.creative_name ?? ''}::${promotion?.promotion_id ?? ''}`

/**
 * If a key has more than one tracked entry (the same banner appears in
 * multiple slot groups on the same page), pick the one whose
 * `creative_slot` starts with the same component prefix the click handler
 * provided. Falls back to the first entry.
 */
const pickBestMatchingTrackedPromotion = (
  trackedEntries: TrackedPromotionEntry[],
  selectedPromotion: PromotionLookupRequest,
): TrackedPromotionEntry => {
  if (trackedEntries.length === 1) return trackedEntries[0]

  // Primary tiebreaker: match on the page-level slot position injected by
  // SlotContent via CurrentSlotPositionContext. This correctly distinguishes
  // the same promotion appearing in two different slots on the same page
  // (e.g. a category tile list in slot 2 and again in slot 5).
  if (selectedPromotion?.slot_position != null) {
    const slotPositionMatch = trackedEntries.find(
      (entry) => entry.slotPosition === selectedPromotion.slot_position
    )
    if (slotPositionMatch) return slotPositionMatch
  }

  // Secondary tiebreaker: match on the component-type prefix of the creative_slot
  // (e.g. "homepage_category-tile"). Used as a fallback when slot_position is unavailable.
  const selectedCreativeSlot = selectedPromotion?.creative_slot ?? ''
  const componentPrefix = selectedCreativeSlot.split('_').slice(0, 2).join('_')

  if (componentPrefix) {
    const prefixMatch = trackedEntries.find((entry) =>
      entry.creativeSlot?.startsWith(componentPrefix),
    )
    if (prefixMatch) return prefixMatch
  }

  return trackedEntries[0]
}

export const PromotionTrackingProvider = ({children}: {children: React.ReactNode}) => {
  const trackedPromotionsRef = useRef(new Map<string, TrackedPromotionEntry[]>())
  const location = useLocation()

  // Always keep a synchronous ref to the current pathname so
  // that registerTrackedPromotions can detect page changes
  const locationPathnameRef = useRef(location.pathname)
  locationPathnameRef.current = location.pathname

  // Tracks which pathname the registry was last populated for. When the first
  // registration arrives for a new page, the registry is cleared before writing.
  const registryPathnameRef = useRef<string | null>(null)

  const registerTrackedPromotions = useCallback((promotionItems: TrackedPromotionItem[]) => {
    if (!Array.isArray(promotionItems) || promotionItems.length === 0) return

    // Clear stale entries when we detect a page change. This happens lazily
    // here rather than in a useEffect so it is guaranteed to run before any
    // new entries are written, regardless of effect ordering.
    if (registryPathnameRef.current !== locationPathnameRef.current) {
      trackedPromotionsRef.current.clear()
      registryPathnameRef.current = locationPathnameRef.current
    }

    const trackedPromotions = trackedPromotionsRef.current

    promotionItems.forEach((promotionItem) => {
      const lookupKey = buildPromotionLookupKey(promotionItem)
      const trackedEntry = {
        slotPosition: promotionItem.slot_position,
        linkPosition: promotionItem.link_position,
        creativeSlot: promotionItem.creative_slot,
      }

      const existingEntries = trackedPromotions.get(lookupKey)
      if (!existingEntries) {
        trackedPromotions.set(lookupKey, [trackedEntry])
        return
      }

      // If an entry for the same slot position already exists, replace it.
      // This ensures that whichever registration fires last (page-level, via
      // a parent useEffect) wins over any earlier component-level registration
      // that may have used a stale local index or slot group name.
      if (trackedEntry.slotPosition != null) {
        const existingIndex = existingEntries.findIndex(
          (entry) => entry.slotPosition === trackedEntry.slotPosition
        )
        if (existingIndex !== -1) {
          existingEntries[existingIndex] = trackedEntry
          return
        }
      } else {
        // For entries without a slot position, fall back to exact-match idempotency.
        const isAlreadyRegistered = existingEntries.some(
          (entry) =>
            entry.slotPosition === trackedEntry.slotPosition &&
            entry.linkPosition === trackedEntry.linkPosition &&
            entry.creativeSlot === trackedEntry.creativeSlot
        )
        if (isAlreadyRegistered) return
      }

      existingEntries.push(trackedEntry)
    })
  }, [])

  const findTrackedPromotion = useCallback((selectedPromotion: PromotionLookupRequest) => {
    const lookupKey = buildPromotionLookupKey(selectedPromotion)
    const trackedEntries = trackedPromotionsRef.current.get(lookupKey)
    if (!trackedEntries || trackedEntries.length === 0) return undefined
    return pickBestMatchingTrackedPromotion(trackedEntries, selectedPromotion)
  }, [])

  return (
    <PromotionTrackingRegistryContext.Provider
      value={{registerTrackedPromotions, findTrackedPromotion}}
    >
      {children}
    </PromotionTrackingRegistryContext.Provider>
  )
}

export const usePromotionTrackingRegistry = (): PromotionTrackingRegistryContextValue =>
  useContext(PromotionTrackingRegistryContext)
